import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  List,
  ListItem,
  ListIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import Header from "../Common/Header";
import Footer from "../Common/Footer";
import { MdCheck } from "react-icons/md";

interface TermsSection {
  id: string;
  title: string;
  content: React.ReactNode;
  subsections?: TermsSection[];
}

const TermsOfUse: React.FC = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("blue.50", "blue.900");
  const importantBg = useColorModeValue("red.50", "red.900");
  const footerBg = useColorModeValue("gray.50", "gray.700");

  const termsData: TermsSection[] = [
    {
      id: "1",
      title: "Acceptance of Terms",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            These Terms and Conditions ("Terms") constitute a legally binding
            agreement between you ("User," "Healthcare Professional," "You,"
            "Your") and VFrequentists LLP ("Company," "We," "Us," "Our")
            governing the use of the QIQ Health mobile application, website, and
            related services (collectively, "QIQ Health" or the "Service").
          </Text>
          <Text>
            By accessing or using QIQ Health, you acknowledge that you have
            read, understood, and agree to be bound by these Terms and our
            Privacy & Security Policy.
          </Text>
          <Box
            bg={importantBg}
            border="2px solid"
            borderColor="red.300"
            borderRadius="md"
            p={4}
          >
            <Text fontWeight="bold" color="red.700">
              IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE QIQ HEALTH.
            </Text>
          </Box>
        </VStack>
      ),
    },
    {
      id: "2",
      title: "Eligibility and Professional Use",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Professional Credentials
            </Heading>
            <Text mb={3}>
              QIQ Health is intended solely for licensed and credentialed
              healthcare professionals, including physicians, nurses, medical
              coders, billing experts, and other qualified practitioners. You
              represent and warrant that you meet all professional licensing and
              credentialing requirements for your jurisdiction and specialty.
            </Text>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Additional Requirements
            </Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Age Requirement:
                </Text>{" "}
                You must be at least 18 years old to create an account and use
                the Service.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Institutional Use:
                </Text>{" "}
                If you access QIQ Health on behalf of a healthcare institution,
                you represent that you have authority to bind the institution to
                these Terms.
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "3",
      title: "License Grant and Restrictions",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Limited License
            </Heading>
            <Text mb={3}>
              Subject to these Terms, VFrequentists LLP grants you a limited,
              non-exclusive, non-transferable, revocable license to access and
              use QIQ Health for professional healthcare purposes within your
              scope of practice.
            </Text>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Prohibited Uses
            </Heading>
            <Text mb={3}>
              You agree{" "}
              <Text as="span" fontWeight="bold">
                not
              </Text>{" "}
              to:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Use QIQ Health for personal, non-professional, or
                consumer-facing healthcare services
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Provide medical advice to patients directly through the Service
                without appropriate clinical context and documentation
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Reverse engineer, decompile, or attempt to extract source code
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Use QIQ Health to violate HIPAA, other privacy regulations, or
                professional ethics
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Upload or transmit any malicious code, malware, or harmful
                content
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Misrepresent your professional credentials or impersonate
                another person
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Reservation of Rights
            </Heading>
            <Text>
              All rights not expressly granted herein are reserved by
              VFrequentists LLP.
            </Text>
          </Box>
        </VStack>
      ),
    },
    {
      id: "4",
      title: "Professional Responsibility and No Medical Advice",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Decision Support Only
            </Heading>
            <Text mb={3}>
              QIQ Health provides decision-support tools and data-management
              features.{" "}
              <Text as="span" fontWeight="bold">
                It does not provide medical advice, diagnosis, or treatment.
              </Text>
            </Text>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Professional Judgment
            </Heading>
            <Text mb={3}>
              You remain solely responsible for all clinical decisions, medical
              coding, and billing determinations. You must independently verify
              all outputs before using them in patient care or revenue-cycle
              workflows.
            </Text>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              No Doctor-Patient Relationship
            </Heading>
            <Text>
              Use of QIQ Health does not create a doctor-patient relationship
              between VFrequentists LLP and any individual.
            </Text>
          </Box>
        </VStack>
      ),
    },
    {
      id: "5",
      title: "User Accounts and Security",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Account Creation
            </Heading>
            <Text>
              You must create an account and provide accurate, complete, and
              current information. You agree to update your information promptly
              if it changes.
            </Text>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Security Responsibilities
            </Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Credentials Security:
                </Text>{" "}
                You are responsible for maintaining the confidentiality of your
                login credentials and for all activities that occur under your
                account.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Unauthorized Use:
                </Text>{" "}
                You must notify us immediately at{" "}
                <Text as="span" fontWeight="bold">
                  security@weqiq.com
                </Text>{" "}
                of any unauthorized use or suspected breach.
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "6",
      title: "Fees and Payment",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            This section outlines the payment terms for QIQ Health services.
          </Text>
          <List spacing={2}>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Subscription Plans:
              </Text>{" "}
              Certain features may require payment of fees. Pricing and billing
              cycles will be disclosed at the time of purchase.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Taxes:
              </Text>{" "}
              You are responsible for all applicable taxes.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Refunds:
              </Text>{" "}
              All fees are non-refundable except as required by law.
            </ListItem>
          </List>
        </VStack>
      ),
    },
    {
      id: "7",
      title: "Intellectual Property",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Company IP
            </Heading>
            <Text>
              QIQ Health and all related content, logos, and intellectual
              property are owned by VFrequentists LLP or its licensors and
              protected by copyright, trademark, and other laws.
            </Text>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              User Content and Feedback
            </Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  User Content:
                </Text>{" "}
                You retain ownership of data you upload, subject to the license
                you grant us to operate the Service.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Feedback:
                </Text>{" "}
                If you provide feedback or suggestions, you grant VFrequentists
                LLP a perpetual, worldwide, royalty-free license to use, modify,
                and incorporate the feedback without compensation to you.
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "8",
      title: "Data Privacy and Security",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            Your use of QIQ Health is governed by the{" "}
            <Text as="span" fontWeight="semibold">
              Security & Privacy Policy
            </Text>{" "}
            (latest revision dated August 11, 2025). By using QIQ Health, you
            consent to the data practices described therein.
          </Text>
        </VStack>
      ),
    },
    {
      id: "9",
      title: "Third-Party Services and Integrations",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            QIQ Health may integrate with third-party services (e.g., EHRs,
            billing systems). Your interactions with third-party services are
            governed solely by the third party's terms. VFrequentists LLP is not
            responsible for third-party content or services.
          </Text>
        </VStack>
      ),
    },
    {
      id: "10",
      title: "Disclaimers",
      content: (
        <VStack align="stretch" spacing={4}>
          <List spacing={2}>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Service Provided "As Is":
              </Text>{" "}
              QIQ Health is provided on an "AS IS" and "AS AVAILABLE" basis
              without warranties of any kind, either express or implied,
              including but not limited to warranties of merchantability,
              fitness for a particular purpose, non-infringement, or course of
              dealing.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                No Warranty of Accuracy:
              </Text>{" "}
              We do not warrant that outputs, data, information, or content
              provided through QIQ Health are accurate, complete, reliable,
              current, error-free, or suitable for any purpose. All outputs must
              be independently verified by qualified healthcare professionals.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                No Warranty of Availability:
              </Text>{" "}
              We do not guarantee uninterrupted, secure, or error-free operation
              of QIQ Health. The Service may be unavailable due to maintenance,
              updates, technical failures, or circumstances beyond our control.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                No Medical Advice:
              </Text>{" "}
              QIQ Health does not provide medical advice, diagnosis, or
              treatment. All clinical decisions remain your sole responsibility.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Regulatory Compliance:
              </Text>{" "}
              While we employ HIPAA-compliant infrastructure and security
              measures, ultimate compliance with HIPAA, state privacy laws, and
              other healthcare regulations in your professional practice remains
              your sole responsibility.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Third-Party Content:
              </Text>{" "}
              We do not warrant or endorse any third-party content, services, or
              products accessible through QIQ Health.
            </ListItem>
          </List>
        </VStack>
      ),
    },
    {
      id: "11",
      title: "Limitation of Liability",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            To the maximum extent permitted by law, VFrequentists LLP, its
            affiliates, officers, directors, employees, agents, suppliers, and
            licensors shall{" "}
            <Text as="span" fontWeight="bold">
              not
            </Text>{" "}
            be liable for any indirect, incidental, special, consequential,
            punitive, or exemplary damages, or for any loss of profits,
            revenues, data, goodwill, business opportunities, or other
            intangible losses, whether based on contract, tort, strict
            liability, or any other legal theory, arising from or related to
            your use of or inability to use QIQ Health, even if we have been
            advised of the possibility of such damages.
          </Text>
          <Text>
            Our total aggregate liability for any claim arising out of or
            relating to these Terms or QIQ Health, regardless of the form of
            action (whether in contract, tort, strict liability, or otherwise),
            shall not exceed the greater of (a) the amount you paid to use the
            Service in the 12 months preceding the claim or (b) one hundred US
            dollars (USD $100). This limitation applies to all claims
            collectively, not per claim.
          </Text>
          <Text>
            Some jurisdictions do not allow the exclusion or limitation of
            certain damages, so some of the above limitations may not apply to
            you. However, to the fullest extent permitted by applicable law, the
            limitations and exclusions set forth above shall apply.
          </Text>
        </VStack>
      ),
    },
    {
      id: "12",
      title: "Indemnification",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text mb={3}>
            You agree to indemnify, defend, and hold harmless VFrequentists LLP,
            its officers, directors, employees, agents, affiliates, suppliers,
            and licensors from and against any and all claims, demands, actions,
            damages, liabilities, losses, costs, and expenses (including
            reasonable attorneys' fees, court costs, and expert witness fees)
            arising out of or related to:
          </Text>
          <List spacing={2}>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Your breach of these Terms or any representation or warranty
              contained herein
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Your professional use of QIQ Health, including all clinical
              decisions, medical coding, billing determinations, and patient
              care decisions
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Your violation of any applicable law, regulation, or professional
              standard, or the rights of any third party
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Your use of any content, data, or information obtained through QIQ
              Health
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Any content you submit, post, transmit, or make available through
              QIQ Health
            </ListItem>
          </List>
        </VStack>
      ),
    },
    {
      id: "13",
      title: "Termination",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              By You
            </Heading>
            <Text>
              You may terminate your account at any time via in-app settings or
              by contacting{" "}
              <Text as="span" fontWeight="bold">
                support@weqiq.com
              </Text>
              .
            </Text>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              By Us
            </Heading>
            <Text>
              We may suspend or terminate your access to QIQ Health at our sole
              discretion for violation of these Terms or potential harm to
              patients, data, or the Service.
            </Text>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Effect of Termination
            </Heading>
            <Text>
              Upon termination, your license to use QIQ Health ceases
              immediately. Certain sections (e.g., Intellectual Property,
              Disclaimers, Limitation of Liability) survive termination.
            </Text>
          </Box>
        </VStack>
      ),
    },
    {
      id: "14",
      title: "Modifications to the Service and Terms",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            We reserve the right to modify or discontinue QIQ Health or to amend
            these Terms at any time. We will provide notice of material changes
            via the Service or email. Continued use after changes become
            effective constitutes acceptance of the revised Terms.
          </Text>
        </VStack>
      ),
    },
    {
      id: "15",
      title: "Governing Law and Dispute Resolution",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text mb={3}>
            These Terms are governed by the laws of{" "}
            <Text as="span" fontWeight="semibold">
              India
            </Text>
            , without regard to conflict of law principles. Any dispute arising
            out of or relating to these Terms shall be resolved through:
          </Text>
          <List spacing={2}>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Good-faith negotiation
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Binding arbitration conducted in{" "}
              <Text as="span" fontWeight="semibold">
                India
              </Text>{" "}
              under the rules of the{" "}
              <Text as="span" fontWeight="semibold">
                Arbitration and Conciliation Act, 2015
              </Text>{" "}
              and administered by the{" "}
              <Text as="span" fontWeight="semibold">
                Indian Council of Arbitration
              </Text>
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              If arbitration is not enforceable, exclusive jurisdiction and
              venue in the courts of{" "}
              <Text as="span" fontWeight="semibold">
                India
              </Text>
            </ListItem>
          </List>
        </VStack>
      ),
    },
    {
      id: "16",
      title: "Miscellaneous",
      content: (
        <VStack align="stretch" spacing={4}>
          <List spacing={2}>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Severability:
              </Text>{" "}
              If any provision is held invalid, the remaining provisions remain
              in full force.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Entire Agreement:
              </Text>{" "}
              These Terms, together with the Privacy & Security Policy,
              constitute the entire agreement between you and VFrequentists LLP
              regarding QIQ Health.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Assignment:
              </Text>{" "}
              You may not assign or transfer your rights under these Terms. We
              may assign our rights without restriction.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Force Majeure:
              </Text>{" "}
              We are not liable for failure to perform due to events beyond our
              reasonable control.
            </ListItem>
          </List>
        </VStack>
      ),
    },
    {
      id: "17",
      title: "Contact Information",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text mb={3}>
            For questions about these Terms, please contact us at:
          </Text>
          <Box
            bg={headerBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            p={4}
          >
            <VStack spacing={2} align="stretch">
              <Text fontWeight="semibold">VFrequentists LLP</Text>
              <Text fontWeight="semibold">
                Attn: Legal Department – QIQ Health
              </Text>
              <Text>
                <Text as="span" fontWeight="semibold">
                  Email:
                </Text>{" "}
                legal@weqiq.com
              </Text>
            </VStack>
          </Box>
        </VStack>
      ),
    },
  ];

  return (
    <Box bg={bgColor} minH="100vh" py={8}>
      <Header minimal={false} />
      <Container maxW="container.xl" mt={10}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box
            textAlign="center"
            pb={8}
            borderBottom="3px solid"
            borderColor="blue.500"
          >
            <Heading as="h1" size="2xl" color="blue.600" mb={2}>
              Terms and Conditions
            </Heading>
            <Heading as="h2" size="lg" color="gray.600" mb={6}>
              QIQ Health by VFrequentists LLP
            </Heading>

            <Box
              bg={headerBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              p={6}
            >
              <VStack spacing={2} align="stretch">
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Effective Date:
                  </Text>{" "}
                  January 1, 2025
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Last Updated:
                  </Text>{" "}
                  August 11, 2025
                </Text>
              </VStack>
            </Box>
          </Box>

          {/* Terms Content */}
          <Accordion allowMultiple defaultIndex={[0]}>
            {termsData.map((section) => (
              <AccordionItem
                key={section.id}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="md"
                mb={4}
              >
                <AccordionButton
                  py={6}
                  px={6}
                  bg={headerBg}
                  _hover={{ bg: "blue.100" }}
                  _expanded={{
                    bg: "blue.200",
                    borderBottom: "2px solid",
                    borderColor: "blue.400",
                  }}
                >
                  <HStack flex="1" textAlign="left" spacing={4}>
                    <Badge colorScheme="blue" fontSize="lg" px={3} py={1}>
                      {section.id}
                    </Badge>
                    <Text fontWeight="semibold" fontSize="lg">
                      {section.title}
                    </Text>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel py={8} px={8}>
                  {section.content}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Footer */}
          <Box
            bg={footerBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            p={8}
          >
            <VStack spacing={6} align="stretch">
              <Text fontStyle="italic" color="gray.600">
                This document has been prepared in accordance with healthcare
                regulations, professional licensing requirements, and
                international legal standards. These Terms constitute a legally
                binding agreement between you and VFrequentists LLP.
              </Text>

              <Divider />

              <VStack spacing={2} align="stretch">
                <Text fontWeight="semibold">VFrequentists LLP</Text>
                <Text fontWeight="semibold">Team QIQ Health</Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Document Version:
                  </Text>{" "}
                  1.0
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Status:
                  </Text>{" "}
                  Final Version - Effective as of January 1, 2025
                </Text>
              </VStack>
            </VStack>
          </Box>
        </VStack>
      </Container>
      <Footer />
    </Box>
  );
};

export default TermsOfUse;
